import { useQuery } from '@tanstack/react-query';
import { useLatestForecastModel } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/hooks';
import { useAdvancedSearchRequestBuilder } from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import AggregationBuilder from 'src/components/BeaconV2Components/utils/AggregationBuilder';
import { useAppSelector } from 'src/utils/Hooks';
import { getAppName } from 'src/utils/app';
import axios from 'axios';

type ForecastField = [string, string, string, string?];

interface UseNewProjectionParams {
  stacklineSku: string | number;
  adjustedFields: ForecastField[];
  unadjustedFields: ForecastField[];
  startWeekId: string;
  endWeekId: string;
  enabled: boolean;
}
export const useNewProjection = ({
  stacklineSku,
  adjustedFields,
  unadjustedFields,
  startWeekId = null,
  endWeekId = null,
  enabled
}: UseNewProjectionParams) => {
  const retailerId = useAppSelector((state) => state.retailer.id);
  const appName = getAppName();
  const { modelVersion, currentPublishVersion } = useLatestForecastModel();
  const mainEntity = useAppSelector((state) => state.entityService.mainEntity);
  const { categoryIds } = mainEntity;

  const adjustedRequestId = 'new-projection-adjusted-forecast';
  const unadjustedRequestId = 'original-projection-unadjusted-forecast';

  const newProjectionAdvancedSearchBuilder = useAdvancedSearchRequestBuilder(
    adjustedRequestId,
    'beacon-adjusted-forecast'
  );
  const originalProjectionAdvancedSearchBuilder = useAdvancedSearchRequestBuilder(
    unadjustedRequestId,
    'beacon-unadjusted-forecast'
  );

  const adjustedAggregationBuilder = new AggregationBuilder('retailerId');
  adjustedAggregationBuilder.addConditionTermFilter('retailerId', [retailerId]);

  const unadjustedAggregationBuilder = adjustedAggregationBuilder.clone();

  adjustedFields.forEach(([aggregateByFieldDisplayName, aggregateByFieldName, func, formula]) => {
    adjustedAggregationBuilder.addAggregationField(
      aggregateByFieldDisplayName,
      aggregateByFieldName,
      func,
      true,
      formula
    );
  });

  unadjustedFields.forEach(([aggregateByFieldDisplayName, aggregateByFieldName, func, formula]) => {
    unadjustedAggregationBuilder.addAggregationField(
      aggregateByFieldDisplayName,
      aggregateByFieldName,
      func,
      true,
      formula
    );
  });

  newProjectionAdvancedSearchBuilder
    .setPageSize(1200)
    .setPeriod('year')
    .setSearchBy('parent')
    .setDoAggregation(true)
    .setReturnDocuments(false)
    .addConditionTermFilter('categoryId', 'should', categoryIds)
    .addConditionTermFilter('forecastModelVersion', 'must', [modelVersion])
    .addConditionTermFilter('publishVersion', 'must', [currentPublishVersion])
    .addConditionTermFilter('stacklineSku', 'should', [stacklineSku])
    .addAggregation(adjustedAggregationBuilder.build())
    .addSortField('Total Traffic', 'totalTraffic', 'sum', true)
    .addConditionRangeFilter('forecastWeekId', Number(startWeekId), Number(endWeekId));

  originalProjectionAdvancedSearchBuilder
    .setPageSize(1200)
    .setPeriod('year')
    .setSearchBy('parent')
    .setDoAggregation(true)
    .setReturnDocuments(false)
    .addConditionTermFilter('categoryId', 'should', categoryIds)
    .addConditionTermFilter('forecastModelVersion', 'must', [modelVersion])
    .addConditionTermFilter('publishVersion', 'must', [currentPublishVersion])
    .addConditionTermFilter('stacklineSku', 'should', [stacklineSku])
    .addAggregation(unadjustedAggregationBuilder.build())
    .addSortField('Total Traffic', 'totalTraffic', 'sum', true)
    .addConditionRangeFilter('forecastWeekId', Number(startWeekId), Number(endWeekId));

  const newProjectionRequest = [newProjectionAdvancedSearchBuilder.build()];
  const originalProjectionRequest = [originalProjectionAdvancedSearchBuilder.build()];
  const query = useQuery({
    queryKey: [stacklineSku, startWeekId, endWeekId, adjustedFields, unadjustedFields],
    queryFn: async () => {
      const adjustedEndpoint = `api/${appName}/AdvancedSearch?_id=${adjustedRequestId}`;
      const unadjustedEndpoint = `api/${appName}/AdvancedSearch?_id=${unadjustedRequestId}`;
      const [{ data: originalResponse }, { data: newResponse }] = await Promise.all([
        axios.post(adjustedEndpoint, newProjectionRequest),
        axios.post(unadjustedEndpoint, originalProjectionRequest)
      ]);

      return [originalResponse[0], newResponse[0]];
    },

    retry: 0,
    enabled,
    staleTime: 0,
    refetchOnWindowFocus: false
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError
  };
};
